/**
 * Citadel — top-level route shell.
 *
 * Routes:
 *   /                           -> Dashboard (admin surface index)
 *   /feature-switches           -> FeatureSwitches (BL-29-1790 toggles)
 *   /templates                  -> TemplatesModeration (BL-29-1828 · BL-29-1834 scaffold)
 *   /run-status-reconciliation  -> RunStatusReconciliation (BL-29-2194 trust-integrity monitor)
 *
 * Sprint: SPR-26.O | BL-29-1790 | SPR-26.N-1 | BL-29-1828 | BL-29-2194
 */
import { NavLink, Navigate, Route, Routes } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
// Synthesis XII-5: Phosphor (thin) — Citadel ops surface, cartographic-instrument feel.
import { Terminal } from 'phosphor-react';
import Dashboard from './pages/Dashboard';
import FeatureSwitches from './pages/FeatureSwitches';
import TemplatesModeration from './pages/TemplatesModeration';
import RunStatusReconciliation from './pages/RunStatusReconciliation';

export default function App() {
    const { isAuthenticated, isLoading, loginWithRedirect, logout, user } =
        useAuth0();

    if (isLoading) {
        return (
            <div className="min-h-screen bg-citadel-bg text-citadel-ink-dim font-mono text-xs flex items-center justify-center">
                <span>booting citadel...</span>
            </div>
        );
    }

    if (!isAuthenticated) {
        return (
            <div className="min-h-screen bg-citadel-bg text-citadel-ink font-mono flex items-center justify-center px-6">
                <div className="max-w-md w-full border border-citadel-border bg-citadel-surface p-6">
                    <div className="flex items-center gap-2 mb-4">
                        <Terminal className="size-4 text-citadel-cyan" weight="thin" aria-hidden />
                        <h1 className="text-sm font-semibold tracking-wider uppercase">
                            Citadel
                        </h1>
                    </div>
                    <p className="text-xs text-citadel-ink-dim mb-5 leading-relaxed">
                        Rodiun internal admin plane. Operational independence
                        from app.rodiun.io. Authentication required.
                    </p>
                    <button
                        type="button"
                        onClick={() => void loginWithRedirect()}
                        className="w-full px-3 py-2 bg-citadel-cyan/10 border border-citadel-cyan/40 text-citadel-cyan hover:bg-citadel-cyan/20 text-xs uppercase tracking-wider font-semibold"
                    >
                        Authenticate
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-citadel-bg text-citadel-ink font-sans">
            <header className="border-b border-citadel-border bg-citadel-surface">
                <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-6">
                        <div className="flex items-center gap-2">
                            <Terminal className="size-4 text-citadel-cyan" weight="thin" aria-hidden />
                            <span className="text-xs font-mono font-semibold uppercase tracking-wider">
                                Citadel
                            </span>
                        </div>
                        <nav className="flex items-center gap-1 text-xs font-mono">
                            <NavLink
                                to="/"
                                end
                                className={({ isActive }) =>
                                    `px-3 py-1.5 ${
                                        isActive
                                            ? 'bg-citadel-cyan/10 text-citadel-cyan'
                                            : 'text-citadel-ink-dim hover:text-citadel-ink'
                                    }`
                                }
                            >
                                Dashboard
                            </NavLink>
                            <NavLink
                                to="/feature-switches"
                                className={({ isActive }) =>
                                    `px-3 py-1.5 ${
                                        isActive
                                            ? 'bg-citadel-cyan/10 text-citadel-cyan'
                                            : 'text-citadel-ink-dim hover:text-citadel-ink'
                                    }`
                                }
                            >
                                Feature switches
                            </NavLink>
                            <NavLink
                                to="/templates"
                                className={({ isActive }) =>
                                    `px-3 py-1.5 ${
                                        isActive
                                            ? 'bg-citadel-cyan/10 text-citadel-cyan'
                                            : 'text-citadel-ink-dim hover:text-citadel-ink'
                                    }`
                                }
                            >
                                Templates
                            </NavLink>
                            <NavLink
                                to="/run-status-reconciliation"
                                className={({ isActive }) =>
                                    `px-3 py-1.5 ${
                                        isActive
                                            ? 'bg-citadel-cyan/10 text-citadel-cyan'
                                            : 'text-citadel-ink-dim hover:text-citadel-ink'
                                    }`
                                }
                            >
                                Trust integrity
                            </NavLink>
                        </nav>
                    </div>
                    <div className="flex items-center gap-3 text-2xs font-mono text-citadel-ink-faint">
                        <span>{user?.email ?? user?.sub}</span>
                        <button
                            type="button"
                            onClick={() =>
                                void logout({
                                    logoutParams: { returnTo: window.location.origin },
                                })
                            }
                            className="px-2 py-1 border border-citadel-border hover:border-citadel-border-strong hover:text-citadel-ink"
                        >
                            sign out
                        </button>
                    </div>
                </div>
            </header>

            <main className="max-w-6xl mx-auto px-6 py-6">
                <Routes>
                    <Route path="/" element={<Dashboard />} />
                    <Route path="/feature-switches" element={<FeatureSwitches />} />
                    <Route path="/templates" element={<TemplatesModeration />} />
                    <Route
                        path="/run-status-reconciliation"
                        element={<RunStatusReconciliation />}
                    />
                    <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
            </main>
        </div>
    );
}
